import {
  View,
  Text,
  ScrollView,
  Image,
  TouchableOpacity,
  ActivityIndicator,
  TextInput,
  KeyboardAvoidingView,
  Platform,
  Linking,
} from "react-native";
import MaterialIcons from "@expo/vector-icons/MaterialIcons";
import { useForm, Controller } from "react-hook-form";
import { Feather } from "@expo/vector-icons";
import Animated, { FadeInDown } from "react-native-reanimated";
import i18n, { availableLocales } from "../../../../locales/i18n";
import { LinearGradient } from "expo-linear-gradient";
import {
  widthPercentageToDP as wp,
  heightPercentageToDP as hp,
} from "react-native-responsive-screen";
import { useState, useRef } from "react";
import { router } from "expo-router";
import { Toast } from "react-native-toast-notifications";
import secureStorage from "../../../../utils/secureStorage";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useDispatch } from "react-redux";
import { authActions } from "../../../../store/slices/authSlice";
import { appActions } from "../../../../store/slices/appSlice";
import customAxios from "../../../../utils/customAxios";
import WebLoginScreen from "../../../../components/web/WebLoginScreen";
import CustomAlert from "../../../../components/common/CustomAlert";
import { signInWithGoogle, statusCodes } from "../../../../utils/googleAuth";

export default function LoginScreen() {
  // Render web version on web platform
  if (Platform.OS === "web") {
    return <WebLoginScreen />;
  }

  const locale = i18n.locale;
  const dispatch = useDispatch();

  // Track if animations have already played to prevent re-triggering on re-renders
  const hasAnimated = useRef(false);
  const enterAnim = hasAnimated.current ? undefined : FadeInDown.duration(400);
  // Mark as animated after first render
  if (!hasAnimated.current) hasAnimated.current = true;

  const [showLanguages, setShowLanguages] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      gmail: "",
      Password: "",
    },
  });

  const [buttonSpinner, setButtonSpinner] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [adminAlert, setAdminAlert] = useState(false);
  const [googleSpinner, setGoogleSpinner] = useState(false);

  const handleGoogleSignIn = async () => {
    setGoogleSpinner(true);
    try {
      const { idToken } = await signInWithGoogle();

      // Send idToken to backend
      const response = await customAxios.post("/auth/google", { idToken });

      // Store tokens
      await AsyncStorage.setItem("accessToken", response.data?.accessToken);
      await secureStorage.setItemAsync(
        "refreshToken",
        response.data.refreshToken,
      );

      // Fetch user details
      const userDetailsResponse = await customAxios.get("/users/check");
      const userInfo = userDetailsResponse.data?.userinfo;

      if (userInfo?.role === "superadmin") {
        await AsyncStorage.removeItem("accessToken");
        await secureStorage.deleteItemAsync("refreshToken");
        setGoogleSpinner(false);
        setAdminAlert(true);
        return;
      }

      dispatch(authActions.setUser(userInfo));

      if (userInfo?.is_verified === false) {
        Toast.show(i18n.t("verifyEmailReminder"), {
          type: "info",
          duration: 6000,
        });
      }

      Toast.show(i18n.t("loginSuccess"), { type: "success" });
      setGoogleSpinner(false);
      router.replace("/(drawer)/start");
    } catch (error: any) {
      setGoogleSpinner(false);
      if (error?.code === statusCodes.SIGN_IN_CANCELLED) {
        // User cancelled — do nothing
        return;
      }
      if (error?.code === statusCodes.IN_PROGRESS) {
        return;
      }
      Toast.show(error?.response?.data?.error || i18n.t("googleSignInError"), {
        type: "danger",
      });
    }
  };

  const handleSignIn = async (data: any) => {
    setButtonSpinner(true);
    try {
      const response = await customAxios.post("/mobile/login", {
        login: data.gmail,
        password: data.Password,
      });

      // Store tokens
      await AsyncStorage.setItem("accessToken", response.data?.accessToken);
      await secureStorage.setItemAsync(
        "refreshToken",
        response.data.refreshToken,
      );

      // Fetch complete user data after successful login
      try {
        const userDetailsResponse = await customAxios.get("/users/check");
        const userInfo = userDetailsResponse.data?.userinfo;

        // Block superadmin from using the mobile app
        if (userInfo?.role === "superadmin") {
          await AsyncStorage.removeItem("accessToken");
          await secureStorage.deleteItemAsync("refreshToken");
          setButtonSpinner(false);
          setAdminAlert(true);
          return;
        }

        // Set complete user data in store
        dispatch(authActions.setUser(userInfo));

        // Show reminder about email verification if not verified
        if (userInfo?.is_verified === false) {
          Toast.show(i18n.t("verifyEmailReminder"), {
            type: "info",
            duration: 6000,
          });
        }

        Toast.show(i18n.t("loginSuccess"), {
          type: "success",
        });

        setButtonSpinner(false);

        // Navigate to start screen and clear navigation stack
        router.replace("/(drawer)/start");
      } catch (userError) {
        Toast.show(i18n.t("errorOccurred"), {
          type: "danger",
        });
        setButtonSpinner(false);
      }
    } catch (error: any) {
      setButtonSpinner(false);
      Toast.show(error?.response?.data?.error || i18n.t("errorOccurred"), {
        type: "danger",
      });
    }
  };
  const ChangeLang = () => {
    setShowLanguages(true);
  };

  const handleLanguageChange = async (languageCode: string) => {
    setShowLanguages(false);
    // Use Redux action to properly persist language
    dispatch(appActions.setLocale(languageCode));
  };
  return (
    <View style={{ flex: 1 }}>
      <CustomAlert
        visible={adminAlert}
        title={i18n.t("adminAccessOnly")}
        message={i18n.t("adminRedirectMessage")}
        icon="shield-checkmark"
        iconColor="#F59E0B"
        onDismiss={() => setAdminAlert(false)}
        buttons={[
          { text: i18n.t("cancel"), style: "cancel" },
          {
            text: i18n.t("openAdminPanel"),
            style: "default",
            onPress: () => Linking.openURL("https://khdame.com/admin/login"),
          },
        ]}
      />
      <LinearGradient
        colors={["#004E6D", "#0596D0"]}
        locations={[0, 1]}
        style={{ flex: 1 }}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === "ios" ? "padding" : "height"}
          style={{ flex: 1 }}
          keyboardVerticalOffset={Platform.OS === "ios" ? 0 : 20}
        >
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{ flexGrow: 1 }}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            {/* Top section with logo and curved bottom */}
            <View
              style={{
                alignItems: "center",
                paddingTop: hp("8%"),
                paddingBottom: hp("6%"),
              }}
            >
              <Image
                style={{
                  width: wp("20%"),
                  height: wp("20%"),
                  tintColor: "white",
                  marginBottom: hp("3%"),
                }}
                source={require("../../../../assets/logo-no-bg.png")}
                resizeMode="contain"
              />

              {/* White curved section */}
              <View
                style={{
                  backgroundColor: "white",
                  width: wp("100%"),
                  minHeight: hp("75%"),
                  borderTopLeftRadius: 60,
                  borderTopRightRadius: 60,
                  paddingHorizontal: wp("8%"),
                  paddingTop: hp("4%"),
                  marginTop: hp("2%"),
                }}
              >
                {/* Title */}
                <Animated.View entering={enterAnim?.delay(100)}>
                  <Text
                    style={{
                      fontSize: 24,
                      fontWeight: "bold",
                      textAlign: "center",
                      color: "#004E6D",
                      marginBottom: hp("4%"),
                      fontFamily: "kawkabBold",
                    }}
                  >
                    {i18n.t("loginTitle")}
                  </Text>
                </Animated.View>

                {/* Form Fields */}
                <Animated.View
                  entering={enterAnim?.delay(200)}
                  style={{ marginBottom: hp("2%") }}
                >
                  <Text
                    style={{
                      fontSize: 16,
                      color: "#666",
                      marginBottom: hp("1%"),
                      textAlign: locale === "ar" ? "right" : "left",
                    }}
                  >
                    {i18n.t("usernameOrEmail")}
                  </Text>
                  <View
                    style={{
                      borderWidth: 1,
                      borderColor: "#E0E0E0",
                      borderRadius: 25,
                      backgroundColor: "#F9F9F9",
                      flexDirection: "row",
                      alignItems: "center",
                      paddingHorizontal: wp("4%"),
                      height: 50,
                    }}
                  >
                    <Feather
                      name="user"
                      size={20}
                      color="#999"
                      style={{ marginRight: 10 }}
                    />
                    <Controller
                      control={control}
                      name="gmail"
                      rules={{
                        required: i18n.t("usernameOrEmailRequired"),
                      }}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <TextInput
                          style={{
                            flex: 1,
                            textAlign: locale === "ar" ? "right" : "left",
                            fontSize: 16,
                            color: "#000",
                          }}
                          placeholder={i18n.t("enterUsernameOrEmail")}
                          placeholderTextColor="#999"
                          autoCapitalize="none"
                          value={value}
                          onChangeText={onChange}
                          onBlur={onBlur}
                          keyboardType="email-address"
                        />
                      )}
                    />
                  </View>
                  {errors.gmail && (
                    <Text
                      style={{
                        color: "red",
                        textAlign: locale === "ar" ? "right" : "left",
                        marginTop: 5,
                      }}
                    >
                      {errors.gmail.message}
                    </Text>
                  )}
                </Animated.View>

                <Animated.View
                  entering={enterAnim?.delay(300)}
                  style={{ marginBottom: hp("3%") }}
                >
                  <Text
                    style={{
                      fontSize: 16,
                      color: "#666",
                      marginBottom: hp("1%"),
                      textAlign: locale === "ar" ? "right" : "left",
                    }}
                  >
                    {i18n.t("password")}
                  </Text>
                  <View
                    style={{
                      borderWidth: 1,
                      borderColor: "#E0E0E0",
                      borderRadius: 25,
                      backgroundColor: "#F9F9F9",
                      flexDirection: "row",
                      alignItems: "center",
                      paddingHorizontal: wp("4%"),
                      height: 50,
                    }}
                  >
                    <Feather
                      name="lock"
                      size={20}
                      color="#999"
                      style={{ marginRight: 10 }}
                    />
                    <Controller
                      control={control}
                      name="Password"
                      rules={{
                        required: i18n.t("passwordRequired"),
                      }}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <TextInput
                          style={{
                            flex: 1,
                            textAlign: locale === "ar" ? "right" : "left",
                            fontSize: 16,
                            color: "#000",
                          }}
                          placeholder="••••••••"
                          placeholderTextColor="#999"
                          autoCapitalize="none"
                          value={value}
                          onChangeText={onChange}
                          onBlur={onBlur}
                          secureTextEntry={!showPassword}
                        />
                      )}
                    />
                    <Feather
                      onPress={() => setShowPassword(!showPassword)}
                      name={showPassword ? "eye" : "eye-off"}
                      size={20}
                      color="#999"
                      style={{ marginLeft: 10 }}
                    />
                  </View>
                  {errors.Password && (
                    <Text
                      style={{
                        color: "red",
                        textAlign: locale === "ar" ? "right" : "left",
                        marginTop: 5,
                      }}
                    >
                      {errors.Password.message}
                    </Text>
                  )}
                </Animated.View>

                {/* Login Button */}
                <Animated.View entering={enterAnim?.delay(400)}>
                  <TouchableOpacity
                    style={{
                      marginBottom: hp("3%"),
                      borderRadius: 25,
                      overflow: "hidden",
                    }}
                    onPress={handleSubmit(handleSignIn)}
                    disabled={buttonSpinner}
                  >
                    <LinearGradient
                      colors={["#004E6D", "#0596D0"]}
                      locations={[0, 1]}
                      style={{
                        paddingVertical: hp("2%"),
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {buttonSpinner ? (
                        <ActivityIndicator size="small" color="white" />
                      ) : (
                        <Text
                          style={{
                            color: "white",
                            textAlign: "center",
                            fontSize: 18,
                            fontWeight: "bold",
                          }}
                        >
                          {i18n.t("loginTitle")}
                        </Text>
                      )}
                    </LinearGradient>
                  </TouchableOpacity>
                </Animated.View>

                {/* Divider */}
                <Animated.View
                  entering={enterAnim?.delay(450)}
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    marginBottom: hp("2%"),
                  }}
                >
                  <View
                    style={{ flex: 1, height: 1, backgroundColor: "#E0E0E0" }}
                  />
                  <Text
                    style={{
                      marginHorizontal: 12,
                      color: "#999",
                      fontSize: 14,
                    }}
                  >
                    {i18n.t("orContinueWith")}
                  </Text>
                  <View
                    style={{ flex: 1, height: 1, backgroundColor: "#E0E0E0" }}
                  />
                </Animated.View>

                {/* Google Sign-In Button */}
                <Animated.View entering={enterAnim?.delay(475)}>
                  <TouchableOpacity
                    style={{
                      marginBottom: hp("3%"),
                      borderRadius: 25,
                      overflow: "hidden",
                      borderWidth: 1,
                      borderColor: "#E0E0E0",
                      backgroundColor: "#FFFFFF",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                      paddingVertical: hp("1.8%"),
                      gap: 10,
                    }}
                    onPress={handleGoogleSignIn}
                    disabled={googleSpinner}
                  >
                    {googleSpinner ? (
                      <ActivityIndicator size="small" color="#004E6D" />
                    ) : (
                      <>
                        <Image
                          source={{
                            uri: "https://developers.google.com/identity/images/g-logo.png",
                          }}
                          style={{ width: 20, height: 20 }}
                          resizeMode="contain"
                        />
                        <Text
                          style={{
                            color: "#333",
                            fontSize: 16,
                            fontWeight: "600",
                          }}
                        >
                          {i18n.t("googleSignIn")}
                        </Text>
                      </>
                    )}
                  </TouchableOpacity>
                </Animated.View>

                {/* Links */}
                <Animated.View
                  entering={enterAnim?.delay(500)}
                  style={{ alignItems: "center", marginBottom: hp("4%") }}
                >
                  <TouchableOpacity
                    onPress={() => router.push("/(routes)/(auth)/signup")}
                  >
                    <Text
                      style={{
                        color: "#004E6D",
                        fontSize: 16,
                        textDecorationLine: "underline",
                        marginBottom: hp("1%"),
                      }}
                    >
                      {i18n.t("donthaveaccount")}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() =>
                      router.push("/(routes)/(auth)/forget-password")
                    }
                  >
                    <Text
                      style={{
                        color: "#666",
                        fontSize: 16,
                      }}
                    >
                      {i18n.t("forgotPassword")}
                    </Text>
                  </TouchableOpacity>
                </Animated.View>

                {/* Language Selection */}
                <Animated.View
                  entering={enterAnim?.delay(600)}
                  style={{ alignItems: "center", marginBottom: hp("2%") }}
                >
                  {!showLanguages && (
                    <TouchableOpacity
                      onPress={ChangeLang}
                      style={{
                        backgroundColor: "#B0B0B0",
                        flexDirection: "row",
                        alignItems: "center",
                        paddingHorizontal: wp("8%"),
                        paddingVertical: hp("1.5%"),
                        borderRadius: 10,
                        gap: 8,
                      }}
                    >
                      <MaterialIcons
                        name="expand-more"
                        size={24}
                        color="black"
                      />
                      <Text
                        style={{
                          color: "black",
                          fontSize: 16,
                          fontWeight: "bold",
                        }}
                      >
                        {i18n.t("language")}
                      </Text>
                      <MaterialIcons name="language" size={24} color="black" />
                    </TouchableOpacity>
                  )}

                  {showLanguages && (
                    <View style={{ width: wp("70%") }}>
                      {availableLocales.map((item) => (
                        <TouchableOpacity
                          key={item.code}
                          onPress={() => handleLanguageChange(item.code)}
                          style={{
                            backgroundColor:
                              i18n.locale === item.code ? "#004E6D" : "#E0E0E0",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "center",
                            paddingHorizontal: wp("6%"),
                            paddingVertical: hp("1.5%"),
                            borderRadius: 10,
                            marginBottom: hp("1%"),
                            elevation: 2,
                            shadowColor: "#000",
                            shadowOffset: {
                              width: 0,
                              height: 1,
                            },
                            shadowOpacity: 0.22,
                            shadowRadius: 2.22,
                          }}
                        >
                          <Text
                            style={{
                              fontWeight: "bold",
                              textAlign: "center",
                              fontSize: 16,
                              color:
                                i18n.locale === item.code ? "white" : "black",
                            }}
                          >
                            {item.name}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  )}
                </Animated.View>
              </View>
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </LinearGradient>
    </View>
  );
}
